"use client"

import * as React from "react"

import { PeriodPicker, PeriodPreset, type PeriodPickerLabels } from "@ibada/ui"

import { useActiveBranchTimezone } from "@/features/auth/hooks/use-active-branch-timezone"
import { useTranslations } from "@/i18n"

/**
 * Phone: the picker as plain grey text under a list's title ("Ce mois-ci ⌄"), with no field background in
 * either theme (the outline button paints a gradient image too, hence `bg-none`).
 */
export const MOBILE_SUBTITLE_PERIOD_PICKER_CLASS =
  "!h-7 rounded-full border-0 bg-transparent bg-none px-0 text-[13px] font-bold text-muted-foreground shadow-none hover:bg-transparent aria-expanded:bg-transparent dark:bg-transparent dark:hover:bg-transparent"

type FinancePeriodPickerProps = Omit<React.ComponentProps<typeof PeriodPicker>, "labels" | "timeZone">

/**
 * The tenant's binding of the shared `PeriodPicker`: its translations and the workspace's timezone.
 *
 * The picker itself moved to `@ibada/ui` — it always documented itself as reusable, but called the tenant's
 * `useTranslations` and `useActiveBranchTimezone` directly, which is what kept it in one app.
 */
export function FinancePeriodPicker(props: FinancePeriodPickerProps) {
  const t = useTranslations("finance.period")
  const timeZone = useActiveBranchTimezone()

  const labels = React.useMemo<PeriodPickerLabels>(
    () => ({
      label: t("label"),
      custom: t("custom"),
      presets: Object.fromEntries(
        Object.values(PeriodPreset).map((preset) => [preset, t(`presets.${preset}` as never)]),
      ),
    }),
    [t],
  )

  return <PeriodPicker {...props} labels={labels} timeZone={timeZone} />
}
